import { useMemo, useState } from 'react';
import {
  BODY_TYPE_COPY,
  ESTILO_ETARIO_COPY,
  CALIDAD_COPY,
  ILUMINACION_COPY,
  SCENE_SETTING_COPY,
  SKIN_LEVEL_COPY,
  necesitaProveedorSinFiltro,
  type ProfileOption,
} from '../creative-profile';
import { Link, useNavigate } from 'react-router-dom';
import { CREDITS_LABEL, formatCredits } from '../units';
import { api, ApiRequestError, useApi, type DashboardData } from '../api';
import { Button, Card, Desplegable, Icon, Loading } from '../components/ui';
import { useAviso } from '../avisos';
import { etiqueta, useT } from '../i18n';
import { EditorDeFicha, type Ficha } from '../components/EditorDeFicha';

/**
 * Cuántas tomas se pueden pedir, copiado de `MIN_PLANNED_SHOTS`/`MAX_PLANNED_SHOTS`.
 *
 * La web no depende de `@vcos/domain` —sólo habla con la API—, así que estos dos números están
 * repetidos. Repetirlos es aceptable; **inventarlos** no lo era: el deslizador ofrecía de 1 a 8
 * contra un servidor que aceptaba de 3 a 6, y arrancaba en 8, así que el formulario sin tocar
 * fallaba. Si el rango cambia allá, cambia acá.
 */
const MIN_PLANNED_SHOTS = 1;
const MAX_PLANNED_SHOTS = 6;

/** Lo que sirve `GET /api/catalogo`. El rango de edad viene de ahí, no escrito acá. */
type OpcionDeCatalogo = { clave: string; es: string; en: string };

type Catalogo = {
  nichos: OpcionDeCatalogo[];
  rasgos: { clave: string; es: string; en: string; grupo: string }[];
  gruposDeRasgo: OpcionDeCatalogo[];
  edad: { minima: number; maxima: number };
  /** Los rasgos que se pueden fijar a mano en vez de dejárselos al modelo. */
  apariencia?: {
    tonosDePiel: OpcionDeCatalogo[];
    coloresDePelo: OpcionDeCatalogo[];
    texturasDePelo: OpcionDeCatalogo[];
    coloresDeOjos: OpcionDeCatalogo[];
    alturas: OpcionDeCatalogo[];
  };
};

/**
 * Cuántos nichos se ofrecen de entrada.
 *
 * Estaban los cuarenta y ocho, y eran un muro: para llegar al botón había que pasar de largo una
 * lista más larga que el formulario entero. Ocho entran en dos renglones y cubren lo que la
 * mayoría iba a elegir; el resto sigue estando, plegado, y el campo libre acepta cualquier cosa.
 */
const NICHOS_VISIBLES = 8;

/**
 * Generar una influencer.
 *
 * ── El formulario que hubo antes ────────────────────────────────────────────────
 * Cuarenta y ocho nichos, cincuenta rasgos y cinco desplegables de estilo, todos abiertos y
 * todos opcionales. Cada uno se había agregado por un motivo bueno —dirigir la edad, poder pedir
 * vitíligo, no depender de que el modelo eligiera el tema— y el conjunto era impracticable:
 * ciento y pico de decisiones antes del botón, ninguna obligatoria, ninguna con un valor que
 * dijera «esto ya está bien».
 *
 * ── La regla nueva ──────────────────────────────────────────────────────────────
 * Lo que tiene un valor por defecto que funciona no necesita estar a la vista; necesita estar
 * disponible. A la vista quedan dos decisiones —de qué habla y qué edad tiene— y un botón. Todo
 * lo demás vive bajo «Más opciones», intacto.
 *
 * Y sigue habiendo un camino de cero decisiones: **Sorprendeme** elige tema, edad y rasgos, y
 * escribe la ficha.
 */
export function NewCreator() {
  const { t, idioma } = useT();
  const aviso = useAviso();
  const navigate = useNavigate();
  const dashboard = useApi<DashboardData>('/api/dashboard');
  const zeroSpend = dashboard.data?.settings.zeroSpendMode === true;
  const catalogo = useApi<Catalogo>('/api/catalogo');
  /*
    Con qué se van a generar los registros sin filtro, que decide si esto cuesta o no.

    ── Por qué hace falta pedir los ajustes acá ────────────────────────────────────
    Porque la respuesta cambió y el aviso se quedó viejo. Decía que `sensual` y `atrevido` salían
    por fal y exigían su clave: fal ya no está en el sistema, así que el aviso mandaba a cargar una
    credencial que no existe y a desactivar el modo USD 0 aunque no hiciera falta.

    Hoy la regla es la de `permisivo()` en el contenedor: si el modelo de contenido elegido es la
    GPU propia, se respeta tal cual —no tiene filtro que aflojar y no cuesta, así que el modo USD 0
    no lo frena—; si no lo es, cae al modelo atrevido de Runware, que sí cobra por imagen.

    Es la misma decisión que toma el servidor, y por eso se mira el mismo ajuste que mira él, en
    vez de repetir la conclusión escrita a mano.
  */
  const ajustes = useApi<{ settings: { contentSetModel: string | null } }>('/api/settings');
  const contenidoEnGpuPropia =
    ajustes.data?.settings.contentSetModel?.startsWith('local-gpu/') === true;

  const [kind, setKind] = useState<'human' | 'character'>('human');

  // ── Lo visible ──
  const [nichos, setNichos] = useState<string[]>([]);
  const [nichoLibre, setNichoLibre] = useState('');
  const [edad, setEdad] = useState(25);

  // ── Lo plegado ──
  const [rasgos, setRasgos] = useState<string[]>([]);
  const [countryNarrative, setCountryNarrative] = useState('');
  const [languages, setLanguages] = useState('es,en');
  const [imageCount, setImageCount] = useState(4);
  const [platforms, setPlatforms] = useState<string[]>(['instagram', 'facebook', 'fanvue']);
  const [skinLevel, setSkinLevel] = useState('calle');
  const [bodyType, setBodyType] = useState('sin_preferencia');
  const [sceneSetting, setSceneSetting] = useState('mezcla');
  // 'frontal' es la luz que estaba clavada en el prompt: una influencer nueva sale como
  // salian todas hasta ahora salvo que se elija otra cosa.
  const [iluminacion, setIluminacion] = useState('frontal');
  // 'limpia' es como salian todas: nitidas y bien expuestas.
  const [calidad, setCalidad] = useState('limpia');
  const [estiloEtario, setEstiloEtario] = useState('veinte');

  /**
   * Los rasgos de apariencia fijados a mano.
   *
   * Uno solo de estado y no cinco: viajan juntos al servidor, se limpian juntos y se comparan
   * juntos en la firma. Cinco `useState` sueltos serían cinco lugares donde acordarse de lo
   * mismo.
   *
   * `sin_preferencia` significa «lo elige el modelo», que es como funcionaba antes de que estas
   * listas existieran, y por eso es el valor inicial de todos.
   */
  const [apariencia, setApariencia] = useState<Record<string, string>>({
    skinTone: 'sin_preferencia',
    hairColor: 'sin_preferencia',
    hairTexture: 'sin_preferencia',
    eyeColor: 'sin_preferencia',
    height: 'sin_preferencia',
  });

  /** Sólo lo que se eligió de verdad. Lo demás no viaja: el servidor ya lo ignoraría. */
  const aparienciaElegida = (): Record<string, string> =>
    Object.fromEntries(
      Object.entries(apariencia).filter(([, valor]) => valor && valor !== 'sin_preferencia'),
    );

  // ── La ficha ──
  const [ficha, setFicha] = useState<Ficha | null>(null);
  /**
   * Con qué opciones se escribió la ficha que está en pantalla.
   *
   * Sin esto, cambiar un rasgo o la edad después de escribirla no producía ninguna señal: la
   * ficha seguía ahí, aparentemente al día, y «Generar» creaba la influencer con el texto
   * anterior. El formulario decía una cosa y el resultado era otra, sin que nada estuviera roto.
   */
  const [firmaDeLaFicha, setFirmaDeLaFicha] = useState<string | null>(null);
  const [violaciones, setViolaciones] = useState<string[]>([]);
  const [advertencias, setAdvertencias] = useState<string[]>([]);
  const [escribiendo, setEscribiendo] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const rangoEdad = catalogo.data?.edad ?? { minima: 18, maxima: 90 };
  const desactualizada = ficha !== null && firmaDeLaFicha !== firmaActualValor();

  /** Lo que de verdad influye en el texto. El registro fotográfico o las plataformas, no. */
  function firmaActualValor(): string {
    return JSON.stringify([
      kind,
      nichos,
      edad,
      rasgos,
      countryNarrative.trim(),
      languages,
      bodyType,
      // Cambiar el color de ojos después de escribir la ficha tiene que marcarla como
      // desactualizada, igual que cambiar un rasgo: es lo mismo que se le pidió al modelo.
      aparienciaElegida(),
    ]);
  }

  const togglePlatform = (platform: string): void => {
    setPlatforms((current) =>
      current.includes(platform)
        ? current.filter((candidate) => candidate !== platform)
        : [...current, platform],
    );
  };

  const listaDeIdiomas = (): string[] =>
    languages
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  /**
   * `firma` viene aparte a propósito.
   *
   * «Sorprendeme» escribe el estado y llama acá en la misma vuelta, y para entonces React
   * todavía no aplicó los `setState`: calcular la firma adentro daría la de **antes** del azar,
   * y la ficha recién nacida aparecería marcada como desactualizada.
   */
  const escribirFicha = async (
    entrada?: Record<string, unknown>,
    firma?: string,
  ): Promise<void> => {
    setEscribiendo(true);
    try {
      const respuesta = await api.post<{
        bible: Ficha;
        violaciones: { message: string }[];
        advertencias: { message: string }[];
      }>(
        '/api/creators/bible-draft',
        entrada ?? {
          kind,
          idioma,
          ...(kind === 'human'
            ? {
                ...(nichos.length ? { niche: nichos.slice(0, 6) } : {}),
                ...(countryNarrative.trim() ? { countryNarrative: countryNarrative.trim() } : {}),
                ...(rasgos.length ? { distinguishingTraits: rasgos.slice(0, 12) } : {}),
                ...(Object.keys(aparienciaElegida()).length
                  ? { apariencia: aparienciaElegida() }
                  : {}),
                fictionalAge: edad,
                bodyType,
              }
            : {}),
          ...(listaDeIdiomas().length ? { languages: listaDeIdiomas() } : {}),
        },
      );

      setFicha(respuesta.bible);
      setFirmaDeLaFicha(firma ?? firmaActualValor());
      setViolaciones(respuesta.violaciones.map((violacion) => violacion.message));
      setAdvertencias(respuesta.advertencias.map((advertencia) => advertencia.message));
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('nueva.sinServidor'));
    } finally {
      setEscribiendo(false);
    }
  };

  /**
   * «Sorprendeme»: el camino de cero decisiones.
   *
   * Escribe el estado además de mandarlo, para que lo elegido quede a la vista: un botón que
   * produce una ficha sin mostrar de dónde salió no se puede ajustar, sólo volver a pulsar.
   */
  const alAzar = async (): Promise<void> => {
    const disponibles = catalogo.data;
    if (!disponibles) return;

    const nichosAlAzar = tomarAlAzar(disponibles.nichos, 1 + Math.floor(Math.random() * 2)).map(
      (nicho) => nicho.clave,
    );
    const rasgosAlAzar = tomarAlAzar(disponibles.rasgos, 1 + Math.floor(Math.random() * 3)).map(
      (rasgo) => rasgo.clave,
    );
    // El techo del azar es 55 y no 90: una influencer de 84 es perfectamente válida, pero es una
    // elección deliberada, no algo que uno quiera que le toque sin haberlo pedido.
    const edadAlAzar = rangoEdad.minima + Math.floor(Math.random() * (55 - rangoEdad.minima));

    setNichos(nichosAlAzar);
    setRasgos(rasgosAlAzar);
    setEdad(edadAlAzar);

    await escribirFicha(
      {
        kind,
        idioma,
        ...(kind === 'human'
          ? {
              niche: nichosAlAzar,
              distinguishingTraits: rasgosAlAzar,
              fictionalAge: edadAlAzar,
              bodyType,
            }
          : {}),
        ...(listaDeIdiomas().length ? { languages: listaDeIdiomas() } : {}),
      },
      JSON.stringify([
        kind,
        nichosAlAzar,
        edadAlAzar,
        rasgosAlAzar,
        countryNarrative.trim(),
        languages,
        bodyType,
      ]),
    );
  };

  const submit = async (): Promise<void> => {
    if (!ficha) {
      aviso.error(t('nueva.necesitaFicha'));
      return;
    }
    setSubmitting(true);

    try {
      const result = await api.post<{ workflowRunId: string }>('/api/creators/generate', {
        imageCount,
        platforms,
        kind,
        idioma,
        // La ficha ya editada viaja entera: el workflow no vuelve a pedirle una al modelo.
        bible: ficha,
        creativeProfile:
          kind === 'character'
            ? // El nivel de piel, la complexión y el estilismo describen un cuerpo humano: para
              // un personaje no dicen nada, y mandarlos ensuciaría su prompt.
              // La iluminación sí viaja: es de la foto, no del sujeto.
              { sceneSetting, iluminacion, calidad, skinLevel: 'calle', bodyType: 'sin_preferencia' }
            : { skinLevel, bodyType, sceneSetting, estiloEtario, iluminacion, calidad },
      });
      navigate(`/workflow/${result.workflowRunId}`);
    } catch (caught) {
      // El error ya salió como aviso flotante: acá sólo se devuelve el botón. Un fallo de red no
      // llega a la API y por eso se avisa a mano; los demás los emite el cliente.
      if (!(caught instanceof ApiRequestError)) {
        aviso.error(t('nueva.sinServidor'));
      }
      setSubmitting(false);
    }
  };

  const capacity = dashboard.data?.costEstimate.remainingCreatorsToday ?? null;
  const outOfQuota = capacity !== null && capacity <= 0;
  const spendEnabled = dashboard.data?.settings.zeroSpendMode === false;
  const profile = dashboard.data?.settings.imageQualityProfile ?? '';
  const perCreator = dashboard.data?.costEstimate.neuronsPerCreator ?? 0;
  const dailyLimit = dashboard.data?.settings.dailyNeuronLimit ?? 0;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t('nueva.titulo')}</h1>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{t('nueva.subtitulo')}</p>
      </div>

      {(dashboard.loading || catalogo.loading) && <Loading />}

      {/*
        Advertencia previa, no error posterior. Si el tope no alcanza, el trabajo se difiere al
        reinicio de las 00:00 UTC en vez de fallar — pero quien administra merece saberlo antes de
        pulsar, no después.
      */}
      {outOfQuota && (
        <div className="rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-4">
          <p className="text-sm font-medium text-[var(--color-warn)]">
            {spendEnabled
              ? t('nueva.topeNoAlcanza')
              : t('nueva.cuotaNoAlcanza')}
          </p>
          <p className="mt-1 text-sm">
            {spendEnabled ? (
              <>
                {t('nueva.conElPerfil')}
                <strong>{profile}</strong>
                {t('nueva.cadaInfluencerCuesta')}
                <strong>{formatCredits(perCreator)}</strong> {CREDITS_LABEL}
                {t('nueva.yTuTopeEsta')}
                <strong>{formatCredits(dailyLimit)}</strong>
                {t('nueva.subiloEn')}
                <Link to="/consumo" className="underline underline-offset-2">
                  {t('nueva.consumoYAjustes')}
                </Link>
                {t('nueva.oGeneraIgual')}
              </>
            ) : (
              <>
                {t('nueva.podesGenerarIgual')}
              </>
            )}
          </p>
        </div>
      )}

      {/* ── Lo único que hay que decidir ────────────────────────────────────── */}
      <Card title={t('nueva.empecemos')}>
        <div className="space-y-6">
          <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                ['human', t('nueva.kind.human'), t('nueva.kind.humanDetalle')],
                ['character', t('nueva.kind.character'), t('nueva.kind.characterDetalle')],
              ] as const
            ).map(([valor, titulo, detalle]) => (
              <button
                key={valor}
                type="button"
                onClick={() => setKind(valor)}
                className={
                  'rounded-xl border p-3 text-left transition ' +
                  (kind === valor
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/5'
                    : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)]')
                }
              >
                <p className="text-sm font-medium">{titulo}</p>
                <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">{detalle}</p>
              </button>
            ))}
          </div>

          {kind === 'character' && (
            <p className="rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-xs text-[var(--color-ink-muted)]">
              {t('nueva.kind.characterAviso')}
            </p>
          )}

          {kind === 'human' && (
            <>
              <div>
                <label className="block text-sm font-medium">{t('nueva.deQueHabla')}</label>
                <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
                  {t('nueva.deQueHablaAyuda')}
                </p>
                <SelectorDeNichos
                  catalogo={catalogo.data?.nichos ?? []}
                  elegidos={nichos}
                  onChange={setNichos}
                  libre={nichoLibre}
                  onLibre={setNichoLibre}
                />
              </div>

              <div>
                <label htmlFor="edad" className="block text-sm font-medium">
                  {t('nueva.edad')}:{' '}
                  <span className="tabular-nums">{t('nueva.edadAnios', { n: edad })}</span>
                </label>
                <input
                  id="edad"
                  type="range"
                  min={rangoEdad.minima}
                  max={rangoEdad.maxima}
                  value={edad}
                  onChange={(event) => setEdad(Number(event.target.value))}
                  className="mt-2 w-full accent-[var(--color-accent)]"
                />
                <div className="flex justify-between text-xs text-[var(--color-ink-muted)]">
                  <span className="tabular-nums">{rangoEdad.minima}</span>
                  <span className="tabular-nums">{rangoEdad.maxima}</span>
                </div>
                {edad <= rangoEdad.minima + 2 && (
                  <p className="mt-2 rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-3 text-xs">
                    {t('nueva.edadCercaDelPiso')}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </Card>

      {/* ── Todo lo demás, plegado ──────────────────────────────────────────── */}
      <Desplegable titulo={t('nueva.masOpciones')} detalle={t('nueva.masOpcionesDetalle')}>
        <div className="space-y-6">
          <p className="text-xs text-[var(--color-ink-muted)]">{t('nueva.antesDeEscribir')}</p>
          {kind === 'human' && (
            <>
              <div>
                <label className="block text-sm font-medium">{t('nueva.rasgos')}</label>
                <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
                  {t('nueva.rasgosAyuda')}
                </p>
                <SelectorDeRasgos
                  rasgos={catalogo.data?.rasgos ?? []}
                  grupos={catalogo.data?.gruposDeRasgo ?? []}
                  elegidos={rasgos}
                  onChange={setRasgos}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="country" className="block text-sm font-medium">
                    {t('nueva.pais')}
                  </label>
                  <input
                    id="country"
                    type="text"
                    value={countryNarrative}
                    onChange={(event) => setCountryNarrative(event.target.value)}
                    placeholder={t('nueva.paisPlaceholder')}
                    className="mt-2 w-full px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="languages" className="block text-sm font-medium">
                    {t('nueva.idiomasDePublicacion')}
                  </label>
                  <input
                    id="languages"
                    type="text"
                    value={languages}
                    onChange={(event) => setLanguages(event.target.value)}
                    className="mt-2 w-full px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {/*
                La apariencia va JUSTO DEBAJO del país, y no es casual.

                Son las dos cosas que deciden cómo se ve, y compiten: el país empuja hacia lo
                típico del lugar y esto lo fija a mano. Separadas en el formulario, quien elige
                un país no se entera de que puede fijar el resto, y quien fija el resto no ve
                contra qué está decidiendo.
              */}
              <SelectorDeApariencia
                catalogo={catalogo.data?.apariencia}
                valores={apariencia}
                onChange={(campo, valor) =>
                  setApariencia((actual) => ({ ...actual, [campo]: valor }))
                }
                pais={countryNarrative.trim()}
              />
            </>
          )}

          <div>
            <label htmlFor="imageCount" className="block text-sm font-medium">
              {t('nueva.imagenesIniciales')}
              <span className="tabular-nums">{imageCount}</span>
            </label>
            {/*
              El recorrido es el que el servidor acepta, ni uno más. Iba de 1 a 8 y arrancaba en
              8, contra un esquema que sólo admite de 1 a 6: las dos puntas eran inválidas y el
              valor por defecto también.
            */}
            <input
              id="imageCount"
              type="range"
              min={MIN_PLANNED_SHOTS}
              max={MAX_PLANNED_SHOTS}
              value={imageCount}
              onChange={(event) => setImageCount(Number(event.target.value))}
              className="mt-2 w-full accent-[var(--color-accent)]"
            />
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
              {imageCount === 1
                ? t('nueva.unaSolaToma')
                : t('nueva.menosImagenes')}
            </p>
          </div>

          <div>
            <p className="text-sm font-medium">{t('nueva.estiloVisual')}</p>
            <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
              {t('nueva.estiloVisualQue')}
            </p>

            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              {(
                [
                  /*
                    El registro y el tono del feed ya no se eligen: todas llevan el mismo look
                    —`LOOK_FIJO`—, que es el que se midió contra fotos reales. Seguían acá
                    después de haber salido de la ficha, prometiendo un control que no hacía
                    nada. En su lugar va el estilismo, que sí cambia el resultado.
                  */
                  [t('ident.estiloEtario'), estiloEtario, setEstiloEtario, ESTILO_ETARIO_COPY],
                  // Nivel de piel y complexión describen un cuerpo humano: para un personaje no
                  // dicen nada y ocuparían lugar prometiendo un control que no existe.
                  ...(kind === 'character'
                    ? []
                    : ([
                        [t('ident.nivelDePiel'), skinLevel, setSkinLevel, SKIN_LEVEL_COPY],
                        [t('ident.complexion'), bodyType, setBodyType, BODY_TYPE_COPY],
                      ] as [string, string, (value: string) => void, readonly ProfileOption[]][])),
                  [t('ident.escenas'), sceneSetting, setSceneSetting, SCENE_SETTING_COPY],
                  // La luz es de la foto, no del sujeto: tambien aplica a un personaje.
                  [t('ident.iluminacion'), iluminacion, setIluminacion, ILUMINACION_COPY],
                  // Aparte de la luz: la misma camara mala de dia y de noche.
                  [t('ident.calidad'), calidad, setCalidad, CALIDAD_COPY],
                ] as [string, string, (value: string) => void, readonly ProfileOption[]][]
              ).map(([label, value, set, options]) => (
                <div key={label}>
                  <label className="block text-xs font-medium">{label}</label>
                  <select
                    value={value}
                    onChange={(event) => set(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-3 py-2 text-sm"
                  >
                    {options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.label)}
                        {/*
                          El aviso va en la etiqueta, no en un `disabled`.

                          Una opción deshabilitada en un desplegable no explica nada: se ve gris y
                          quien la quería se queda sin saber por qué. Nombrar el freno deja elegirla
                          igual y dice qué hacer para levantarlo.

                          El freno ya no es una credencial que falta —fal salió del sistema— sino el
                          gasto: estos dos registros cuestan sólo cuando NO se generan con la GPU
                          propia. Con ella no aparece nada, porque no hay nada que arreglar.
                        */}
                        {label === t('ident.nivelDePiel') &&
                        necesitaProveedorSinFiltro(option.value) &&
                        !contenidoEnGpuPropia &&
                        zeroSpend
                          ? t('nueva.conModoCeroActivo')
                          : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
                    {(() => {
                      // Las opciones traen CLAVES, no texto. Ver `creative-profile.ts`.
                      const elegida = options.find((option) => option.value === value);
                      return elegida ? t(elegida.detail) : null;
                    })()}
                  </p>
                </div>
              ))}
            </div>

            {skinLevel === 'playa' && (
              <p className="mt-3 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-xs text-[var(--color-ink-muted)]">
                {t('nueva.trajeDeBanio')}
              </p>
            )}

            {/*
              El aviso no es una advertencia moral: es el único registro que cambia de modelo, y lo
              que cambia con él es si la generación cuesta.

              Decía que este nivel «genera con fal, no con Google» y que hacía falta la clave de
              fal. Las dos cosas eran falsas: fal salió del sistema hace tiempo y `tieneFal` estaba
              fijo en `true`, así que la mitad del aviso era código muerto que igual se mostraba.
            */}
            {(skinLevel === 'sensual' || skinLevel === 'atrevido') && (
              <p className="mt-3 rounded-xl border border-[var(--color-accent)]/40 bg-[var(--color-surface-sunken)] p-3 text-xs text-[var(--color-ink-muted)]">
                {t('nueva.necesitaSinFiltroAntes')}
                <strong>{t('nueva.modeloSinFiltro')}</strong>
                {t('nueva.necesitaSinFiltroDespues')}
                <br />
                <br />
                {contenidoEnGpuPropia ? (
                  <>
                    {t('nueva.gpuPropiaAntes')}
                    <strong>{t('nueva.tuPropiaGpu')}</strong>
                    {t('nueva.gpuPropiaDespues')}
                  </>
                ) : (
                  <>
                    {t('nueva.gpuPropiaAntes')}
                    <strong>Runware</strong>
                    {t('nueva.runwareMedio1')}
                    <strong>{t('nueva.cobraPorImagen')}</strong>
                    {t('nueva.runwareMedio2')}
                    <strong>{t('nueva.desactivadoModoCero')}</strong>
                    {t('nueva.runwareFin')}
                    <br />
                    <br />
                    {t('nueva.sinGastarAntes')}
                    <Link to="/modelos" className="underline">
                      {t('mod.titulo')}
                    </Link>
                    .
                  </>
                )}
                {/*
                  Y esto es lo que falta **en esta cuenta**, no lo que podría faltar en general.
                  Lo de arriba explica el mecanismo; esto dice si podés generar ahora mismo — y por
                  eso no aparece con la GPU propia, donde no hay nada que destrabar.
                */}
                {!contenidoEnGpuPropia && zeroSpend && (
                  <>
                    <br />
                    <br />
                    <strong className="text-[var(--color-warn)]">
                      {t('nueva.todaviaNoPodes')}
                    </strong>
                    {t('nueva.modoCeroActivoEn')}
                    <Link to="/consumo" className="underline">
                      {t('cred.consumo')}
                    </Link>
                    {t('nueva.oEligiendoGpu')}
                    <Link to="/modelos" className="underline">
                      {t('mod.titulo')}
                    </Link>
                    .
                  </>
                )}
                {!contenidoEnGpuPropia && (
                  <>
                    <br />
                    <br />
                    {t('nueva.rechazaEscenasAntes')}
                    <strong>{t('nueva.cadaRechazoSePaga')}</strong>
                    {t('nueva.rechazaEscenasDespues')}
                    {skinLevel === 'atrevido' && t('nueva.dondeMasPasa')}
                  </>
                )}
                <br />
                <br />
                {t('nueva.desnudezProhibida')}
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium">{t('nueva.plataformas')}</p>
            <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
              {t('nueva.plataformasQue')}
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              {(['instagram', 'facebook', 'fanvue'] as const).map((platform) => (
                <label key={platform} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={platforms.includes(platform)}
                    onChange={() => togglePlatform(platform)}
                    className="accent-[var(--color-accent)]"
                  />
                  {platform}
                </label>
              ))}
            </div>
          </div>

          <p className="rounded-xl border border-[var(--color-border-subtle)] p-3 text-xs text-[var(--color-ink-muted)]">
            {t('nueva.modoCeroEstaAntes')}
            <strong>
              {t(
                dashboard.data?.settings.zeroSpendMode === false
                  ? 'nueva.desactivado'
                  : 'nueva.activo',
              )}
            </strong>
            {t('nueva.modoCeroEstaDespues')}
          </p>
        </div>
      </Desplegable>

      {/*
        El botón va DESPUÉS de las opciones, porque las opciones son lo que lee.

        Estaba antes: se pulsaba «Escribir la ficha» y recién más abajo aparecía «Más opciones»
        con los rasgos adentro — que son justamente una entrada de la ficha. Quien los elegía
        después se quedaba con el texto anterior sin ninguna señal de que había que volver a
        pedirlo. El orden de la pantalla contradecía el orden real de las cosas.
      */}
      {!ficha && (
        <div className="flex flex-wrap items-center justify-center gap-3 py-2">
          <Button disabled={escribiendo} onClick={() => void escribirFicha()}>
            {escribiendo ? t('nueva.escribiendo') : t('nueva.escribirFicha')}
          </Button>
          <span className="text-xs text-[var(--color-ink-muted)]">{t('nueva.oBien')}</span>
          <button
            type="button"
            disabled={escribiendo || !catalogo.data}
            onClick={() => void alAzar()}
            className="flex items-center gap-1.5 text-sm text-[var(--color-accent)] underline underline-offset-2 disabled:opacity-50"
          >
            <Icon name="dice" size={14} />
            {t('nueva.alAzar')}
          </button>
        </div>
      )}

      {/* ── La ficha, cuando existe ─────────────────────────────────────────── */}
      {escribiendo && <Loading label={t('nueva.escribiendo')} />}

      {ficha && !escribiendo && (
        <Card
          title={t('nueva.paso2')}
          actions={
            <>
              <button
                type="button"
                disabled={!catalogo.data}
                onClick={() => void alAzar()}
                className="flex items-center gap-1.5 text-sm text-[var(--color-accent)] underline underline-offset-2 disabled:opacity-50"
              >
                <Icon name="dice" size={14} />
                {t('nueva.alAzar')}
              </button>
              <Button variant="secondary" onClick={() => void escribirFicha()}>
                {t('nueva.reescribirFicha')}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {/*
              La ficha no se rehace sola al cambiar una opción — cuesta una llamada al modelo y
              pisaría lo que la persona acaba de editar a mano. Pero callarlo sería peor: se
              crearía la influencer con el texto anterior y la pantalla no habría dicho nada.
            */}
            {desactualizada && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-3">
                <p className="text-xs">{t('nueva.opcionesCambiaron')}</p>
                <button
                  type="button"
                  onClick={() => void escribirFicha()}
                  className="shrink-0 text-xs font-medium text-[var(--color-accent)] underline underline-offset-2"
                >
                  {t('nueva.volverAEscribir')}
                </button>
              </div>
            )}

            {violaciones.length > 0 && (
              <div className="rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-3">
                <p className="text-sm font-medium text-[var(--color-danger)]">
                  {t('nueva.fichaViolaciones')}
                </p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                  {violaciones.map((violacion) => (
                    <li key={violacion}>{violacion}</li>
                  ))}
                </ul>
              </div>
            )}
            {advertencias.length > 0 && (
              <div className="rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-3">
                <p className="text-sm font-medium text-[var(--color-warn)]">
                  {t('nueva.fichaAdvertencias')}
                </p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                  {advertencias.map((advertencia) => (
                    <li key={advertencia}>{advertencia}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-[var(--color-ink-muted)]">{t('nueva.fichaEditable')}</p>
            <EditorDeFicha ficha={ficha} onChange={setFicha} />
          </div>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={() => navigate('/')}>
          {t('comun.cancelar')}
        </Button>
        <Button onClick={() => void submit()} disabled={submitting || !ficha}>
          {submitting ? t('nueva.generando') : t('nueva.generar')}
        </Button>
      </div>
    </div>
  );
}

/** Toma `cuantos` elementos distintos al azar, sin repetir. */
function tomarAlAzar<T>(lista: readonly T[], cuantos: number): T[] {
  const copia = [...lista];
  const elegidos: T[] = [];
  while (elegidos.length < cuantos && copia.length > 0) {
    const [tomado] = copia.splice(Math.floor(Math.random() * copia.length), 1);
    if (tomado !== undefined) elegidos.push(tomado);
  }
  return elegidos;
}

/** Ocho sugerencias, el resto plegado, y el campo libre siempre disponible. */
function SelectorDeNichos({
  catalogo,
  elegidos,
  onChange,
  libre,
  onLibre,
}: {
  catalogo: { clave: string; es: string; en: string }[];
  elegidos: string[];
  onChange: (elegidos: string[]) => void;
  libre: string;
  onLibre: (valor: string) => void;
}) {
  const { t, idioma } = useT();
  const [verTodos, setVerTodos] = useState(false);

  const visibles = verTodos ? catalogo : catalogo.slice(0, NICHOS_VISIBLES);

  /** Lo escrito a mano no está en el catálogo, así que se muestra aparte para poder quitarlo. */
  const propios = elegidos.filter((clave) => !catalogo.some((nicho) => nicho.clave === clave));

  const alternar = (clave: string): void => {
    onChange(
      elegidos.includes(clave)
        ? elegidos.filter((actual) => actual !== clave)
        : [...elegidos, clave].slice(0, 6),
    );
  };

  const agregarLibre = (): void => {
    const valor = libre.trim();
    if (valor && !elegidos.includes(valor)) onChange([...elegidos, valor].slice(0, 6));
    onLibre('');
  };

  return (
    <div className="mt-2 space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {visibles.map((nicho) => (
          <Chip
            key={nicho.clave}
            activo={elegidos.includes(nicho.clave)}
            onClick={() => alternar(nicho.clave)}
          >
            {etiqueta(nicho, idioma)}
          </Chip>
        ))}
        {propios.map((propio) => (
          <Chip key={propio} activo onClick={() => alternar(propio)}>
            {propio}
          </Chip>
        ))}
        {catalogo.length > NICHOS_VISIBLES && (
          <button
            type="button"
            onClick={() => setVerTodos(!verTodos)}
            className="rounded-full px-2 py-1 text-xs text-[var(--color-accent)] underline underline-offset-2"
          >
            {verTodos ? t('nueva.nichoVerMenos') : t('nueva.nichoVerMas')}
          </button>
        )}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={libre}
          onChange={(event) => onLibre(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              agregarLibre();
            }
          }}
          placeholder={t('nueva.nichoPlaceholder')}
          aria-label={t('nueva.nichoLibre')}
          className="w-full px-3 py-2 text-sm"
        />
        <Button variant="secondary" onClick={agregarLibre} disabled={!libre.trim()}>
          +
        </Button>
      </div>
    </div>
  );
}

/**
 * Los rasgos de apariencia que se fijan a mano.
 *
 * ── Por qué existe ────────────────────────────────────────────────────────────
 * Porque el modelo, librado a su criterio, devuelve siempre lo mismo: pedida la misma ficha con
 * cinco países, salía piel «olive» y pelo ondulado castaño en los cinco, de Rusia a Nigeria.
 *
 * ── Por qué son desplegables y no texto libre ─────────────────────────────────
 * Es la misma razón que los nichos: un campo libre invita a «ojos color miel con destellos
 * dorados», que es una frase de novela y no un valor repetible. `basePrompt` se repite literal
 * en CADA generación, así que lo que entra ahí tiene que ser corto y estable.
 *
 * ── Y por qué se ve cuánto se fijó ────────────────────────────────────────────
 * Porque son cinco perillas plegadas entre otras diez. Sin el contador, quien fijó los ojos hace
 * tres influencers no tiene forma de saber que sigue fijado salvo abriendo y mirando una por una
 * — que es exactamente cómo se generan sorpresas.
 */
function SelectorDeApariencia({
  catalogo,
  valores,
  onChange,
  pais,
}: {
  catalogo:
    | {
        tonosDePiel: OpcionDeCatalogo[];
        coloresDePelo: OpcionDeCatalogo[];
        texturasDePelo: OpcionDeCatalogo[];
        coloresDeOjos: OpcionDeCatalogo[];
        alturas: OpcionDeCatalogo[];
      }
    | undefined;
  valores: Record<string, string>;
  onChange: (campo: string, valor: string) => void;
  pais: string;
}) {
  const { t } = useT();
  const { idioma } = useT();
  if (!catalogo) return null;

  const campos: { clave: string; etiqueta: string; opciones: OpcionDeCatalogo[] }[] = [
    { clave: 'skinTone', etiqueta: t('nueva.tonoDePiel'), opciones: catalogo.tonosDePiel },
    { clave: 'hairColor', etiqueta: t('nueva.colorDePelo'), opciones: catalogo.coloresDePelo },
    { clave: 'hairTexture', etiqueta: t('nueva.texturaDePelo'), opciones: catalogo.texturasDePelo },
    { clave: 'eyeColor', etiqueta: t('nueva.colorDeOjos'), opciones: catalogo.coloresDeOjos },
    { clave: 'height', etiqueta: 'Altura', opciones: catalogo.alturas },
  ];

  const fijados = campos.filter(
    ({ clave }) => valores[clave] && valores[clave] !== 'sin_preferencia',
  );

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label className="block text-sm font-medium">Apariencia</label>
        {fijados.length > 0 && (
          <span className="text-xs text-[var(--color-accent)]">
            {fijados.length} fijado{fijados.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
        {t('nueva.sinPreferenciaAntes')}
        {pais ? t('nueva.segunLaUbicacion', { pais }) : t('nueva.alEscribirLaFicha')}
        {t('nueva.sinPreferenciaDespues')}
      </p>

      <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {campos.map(({ clave, etiqueta: nombre, opciones }) => {
          const fijado = valores[clave] && valores[clave] !== 'sin_preferencia';
          return (
            <div key={clave}>
              <label
                htmlFor={`apariencia-${clave}`}
                className="block text-xs text-[var(--color-ink-muted)]"
              >
                {nombre}
              </label>
              <select
                id={`apariencia-${clave}`}
                value={valores[clave] ?? 'sin_preferencia'}
                onChange={(event) => onChange(clave, event.target.value)}
                /*
                  El borde marca lo fijado. Cinco desplegables iguales no dejan ver de un vistazo
                  cuáles se tocaron, y «sin preferencia» se lee igual que una elección real.
                */
                className={`mt-1 w-full rounded-xl border bg-transparent px-3 py-2 text-sm ${
                  fijado
                    ? 'border-[var(--color-accent)]/60'
                    : 'border-[var(--color-border-subtle)]'
                }`}
              >
                {opciones.map((opcion) => (
                  <option key={opcion.clave} value={opcion.clave}>
                    {etiqueta(opcion, idioma)}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Rasgos distintivos, un grupo por vez.
 *
 * Estaban los seis grupos abiertos: cincuenta fichas de golpe, que es una lista para recorrer y
 * no un menú para elegir. Ahora se abre el grupo que interesa — quien busca vitíligo abre «Piel»,
 * y quien no busca nada no ve ninguno.
 */
function SelectorDeRasgos({
  rasgos,
  grupos,
  elegidos,
  onChange,
}: {
  rasgos: { clave: string; es: string; en: string; grupo: string }[];
  grupos: { clave: string; es: string; en: string }[];
  elegidos: string[];
  onChange: (elegidos: string[]) => void;
}) {
  const { t, idioma } = useT();
  const [abierto, setAbierto] = useState<string | null>(null);

  const porGrupo = useMemo(
    () =>
      grupos.map((grupo) => ({
        grupo,
        rasgos: rasgos.filter((rasgo) => rasgo.grupo === grupo.clave),
      })),
    [grupos, rasgos],
  );

  const alternar = (clave: string): void => {
    onChange(
      elegidos.includes(clave)
        ? elegidos.filter((actual) => actual !== clave)
        : [...elegidos, clave].slice(0, 12),
    );
  };

  /** Lo elegido siempre a la vista, aunque su grupo esté cerrado: si no, se olvida. */
  const elegidosVisibles = rasgos.filter((rasgo) => elegidos.includes(rasgo.clave));

  return (
    <div className="mt-2 space-y-3">
      {elegidosVisibles.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {elegidosVisibles.map((rasgo) => (
            <Chip key={rasgo.clave} activo onClick={() => alternar(rasgo.clave)}>
              {etiqueta(rasgo, idioma)}
            </Chip>
          ))}
          <button
            type="button"
            onClick={() => onChange([])}
            className="px-2 text-xs text-[var(--color-ink-muted)] underline underline-offset-2"
          >
            {t('nueva.rasgosLimpiar')}
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {porGrupo.map(({ grupo, rasgos: delGrupo }) =>
          delGrupo.length === 0 ? null : (
            <Chip
              key={grupo.clave}
              activo={abierto === grupo.clave}
              onClick={() => setAbierto(abierto === grupo.clave ? null : grupo.clave)}
            >
              {etiqueta(grupo, idioma)}
            </Chip>
          ),
        )}
      </div>

      {abierto && (
        <div className="flex flex-wrap gap-1.5 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3">
          {rasgos
            .filter((rasgo) => rasgo.grupo === abierto)
            .map((rasgo) => (
              <Chip
                key={rasgo.clave}
                activo={elegidos.includes(rasgo.clave)}
                onClick={() => alternar(rasgo.clave)}
              >
                {etiqueta(rasgo, idioma)}
              </Chip>
            ))}
        </div>
      )}
    </div>
  );
}

/** Una opción que se enciende y se apaga. Es un botón, no un `div`: el teclado ya funciona. */
function Chip({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={
        'rounded-full border px-3 py-1 text-xs transition ' +
        (activo
          ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
          : 'border-[var(--color-border-subtle)] text-[var(--color-ink-muted)] hover:border-[var(--color-border-strong)] hover:text-[var(--color-ink)]')
      }
    >
      {children}
    </button>
  );
}
